import { giftCardConfigSchema, giftCardLookupSchema } from '~/schemas'
import { fetchApi } from './helpers'

const pickValue = <T>(...values: T[]) => values.find((value) => value !== undefined && value !== null)

const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null

  const normalizedValue =
    typeof value === 'string' ? Number(value.trim()) : Number(value)

  return Number.isFinite(normalizedValue) ? normalizedValue : null
}

export const fetchGiftCardProductData = async () => {
  const response: any = await fetchApi('/gift-cards/product')
  const payload = response.data ?? response

  return giftCardConfigSchema.parse({
    productId: pickValue(
      payload.product_id,
      payload.productId,
      payload.product?.data?.id,
    ),
    type: pickValue(payload.type, payload.product?.data?.type),
    currency: pickValue(
      payload.currency,
      payload.product?.currency?.data,
      payload.product?.currencies?.[0]?.data,
    ),
    presetValues: pickValue(
      payload.preset_values,
      payload.presetValues,
      [],
    ),
    minValue: pickValue(
      payload.min_value,
      payload.minValue,
      null,
    ),
    maxValue: pickValue(
      payload.max_value,
      payload.maxValue,
      null,
    ),
  })
}

export const fetchGiftCardLookupData = async (code: string) => {
  const response: any = await fetchApi(`/gift-cards/${encodeURIComponent(code)}`)
  const extra = response.extra ?? response.data?.extra ?? {}

  const usageHistory = pickValue(extra.usage_history, extra.usageHistory, [])

  const currentValue = pickValue(
    response.current_value,
    response.currentValue,
    response.balance,
    response.remaining_value,
    response.remainingValue,
    response.data?.current_value,
    response.data?.currentValue,
    response.data?.balance,
    response.data?.remaining_value,
    response.data?.remainingValue,
    null,
  )

  let derivedBalance = toFiniteNumber(currentValue)

  return giftCardLookupSchema.parse({
    currency: pickValue(extra.currency, response.currency, response.data?.currency),
    code: pickValue(response.code, response.data?.code),
    status: pickValue(response.status, response.data?.status),
    expiryDate: pickValue(
      response.expiry_date,
      response.expiryDate,
      response.expires_at,
      response.expiresAt,
      response.data?.expiry_date,
      response.data?.expiryDate,
      response.data?.expires_at,
      response.data?.expiresAt,
      null,
    ),
    originalValue: pickValue(
      response.original_value,
      response.originalValue,
      response.value,
      response.data?.original_value,
      response.data?.originalValue,
      response.data?.value,
      null,
    ),
    currentValue,
    usageHistory: Array.isArray(usageHistory)
      ? usageHistory.map((entry: Record<string, any>, index: number) => {
        const amount = toFiniteNumber(
          pickValue(entry.amount, entry.value, entry.data?.amount, entry.data?.value, null),
        )
        const explicitBalance = toFiniteNumber(
          pickValue(
            entry.balance,
            entry.remaining_balance,
            entry.remainingBalance,
            entry.data?.balance,
            entry.data?.remaining_balance,
            entry.data?.remainingBalance,
            null,
          ),
        )
        const balance = explicitBalance ?? derivedBalance

        if (derivedBalance !== null && amount !== null) {
          derivedBalance += amount
        }

        return {
          id: pickValue(entry.id, entry.data?.id, `${index}`),
          createdAt: pickValue(
            entry.created_at,
            entry.createdAt,
            entry.used_at,
            entry.usedAt,
            entry.data?.created_at,
            entry.data?.createdAt,
            null,
          ),
          amount,
          balance,
          description: pickValue(
            entry.description,
            entry.note,
            entry.type,
            entry.data?.description,
            entry.data?.note,
            null,
          ),
          orderNumber: pickValue(
            entry.order_number,
            entry.orderNumber,
            entry.order?.data?.order_number,
            entry.data?.order_number,
            entry.data?.orderNumber,
            null,
          ),
        }
      })
      : [],
  })
}

export type GiftCardProductData = Awaited<ReturnType<typeof fetchGiftCardProductData>>
export type GiftCardLookupData = Awaited<ReturnType<typeof fetchGiftCardLookupData>>
